import {
  getBootstrapPromise,
  hasBootstrapFailed,
  invalidateAuthBootstrap,
  isBootstrapComplete,
  markBootstrapFailure,
  markBootstrapSuccess,
  resetBootstrapForTesting,
  setBootstrapPromise,
} from "./bootstrapState";

export {
  hasBootstrapFailed,
  invalidateAuthBootstrap,
  isBootstrapComplete,
  resetBootstrapForTesting,
};

const BACKGROUND_BOOTSTRAP_MAX_ATTEMPTS = 2;
const BACKGROUND_BOOTSTRAP_BACKOFF_MS = 250;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function loadDefaultStore() {
  const { default: DefaultStore } = await import("@/store/Store");
  return DefaultStore;
}

async function runBootstrapAttempt() {
  const DefaultStore = await loadDefaultStore();
  await DefaultStore.authStore.getGlobalInformation({ forceRefresh: false, fetchProfile: false });
  markBootstrapSuccess();
}

async function ensureAuthBootstrap(maxAttempts = 1) {
  if (isBootstrapComplete()) return;
  const inFlight = getBootstrapPromise();
  if (inFlight) {
    await inFlight;
    return;
  }

  const attempt = (async () => {
    let lastError;
    for (let attemptNumber = 1; attemptNumber <= maxAttempts; attemptNumber += 1) {
      try {
        await runBootstrapAttempt();
        return;
      } catch (error) {
        markBootstrapFailure();
        lastError = error;
        if (attemptNumber < maxAttempts) {
          await sleep(BACKGROUND_BOOTSTRAP_BACKOFF_MS * attemptNumber);
        }
      }
    }
    throw lastError;
  })();
  setBootstrapPromise(attempt);

  try {
    await attempt;
  } finally {
    // Release in-flight handle so failed bootstrap can be retried by the next caller.
    setBootstrapPromise(null);
  }
}

export async function awaitAuthBootstrap() {
  await ensureAuthBootstrap(1);
}

export function scheduleBootstrapInBackground() {
  void ensureAuthBootstrap(BACKGROUND_BOOTSTRAP_MAX_ATTEMPTS).catch(() => {});
}

export async function refreshAuthSession() {
  const DefaultStore = await loadDefaultStore();
  try {
    await DefaultStore.authStore.getGlobalInformation({ forceRefresh: true, fetchProfile: true });
    markBootstrapSuccess();
  } catch (error) {
    markBootstrapFailure();
    throw error;
  }
}
